import { ACCESS_TOKEN_LOCAL_STORAGE_KEY, DECIMAL_FORMAT } from 'constant';
import { ActionCreatorsType, AxiosResponseType, CurrencyFormatType } from 'types';
import { actions as globalStateActions } from '../context/GlobalState/GlobalStateContext';
import { ShowAlertMessageProps } from './types';

export const ensureHTTPs = (url: string) => {
  return url.startsWith('https://') ? url : `https://${url}`;
};

export const getNestedValue = (values: any, fieldName: string) => {
  const fieldNameParts = fieldName.split('.');
  let fieldValue = values;
  for (const part of fieldNameParts) {
    fieldValue = fieldValue?.[part];
  }
  return fieldValue;
};

export const ActionCreatorsFactory = <T, U>(
  settings: { moduleName: string },
  actions: { [key: string]: AxiosResponseType },
): ActionCreatorsType<T, U> => {
  const { moduleName } = settings;

  const result: AxiosResponseType = {};

  Object.keys(actions).forEach((actionName) => {
    const type = `${moduleName}/${actionName}`;

    result[actionName] = (payload?: AxiosResponseType) => ({ type, payload });
    result[actionName].type = type;
    result[type] = (state: T, payload?: AxiosResponseType) => actions[actionName](state, payload);
  });

  return result;
};

export const checkUserAuth = (): string => {
  const user = localStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
  if (!user) return '';

  try {
    const parsedUser = JSON.parse(user);
    const token = parsedUser?.token;

    if (typeof token === 'string') return token;
    return token?.jwtToken || '';
  } catch (error) {
    return '';
  }
};

export const openInNewTab = (url: string) => {
  const newWindow = window.open(url || '', '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

export const formatPrice = (price: number | bigint, style: CurrencyFormatType = DECIMAL_FORMAT): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style,
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter?.format(price) || '0.00';
};

export const generateRandomId = () => {
  const length = 6;
  let result = Math.random().toString(36).substr(2, length);
  while (result.length < length) {
    result += Math.random()
      .toString(36)
      .substr(2, length - result.length);
  }
  return result;
};

export const showAlertMessage = ({
  message,
  warningType,
  dispatch,
  handler,
  modalId = generateRandomId(),
  displayMode = 'full-width-banner',
  autoHideDuration = 4000,
}: ShowAlertMessageProps) => {
  handler && handler();

  const alertData = {
    id: modalId,
    message,
    type: warningType,
    displayMode,
    isOpen: true,
  };

  dispatch(globalStateActions.openAlert(alertData));

  setTimeout(() => {
    dispatch(globalStateActions?.closeAlert({ id: alertData.id }));
  }, autoHideDuration);

  setTimeout(() => {
    dispatch(globalStateActions?.clearAlert({ id: alertData.id }));
  }, autoHideDuration + 1000);
};

export const getImagesGalleryArray = (imagesResponseArray: AxiosResponseType) =>
  imagesResponseArray.map((url: string) => ({
    src: `https://${url}`,
    alt: 'image',
  }));

export const replaceEmptyDataWithNull = (values: AxiosResponseType) => {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      value || (typeof value !== 'string' && typeof value !== 'undefined') ? value : null,
    ]),
  );
};

export const safeToLowerCase = (input: string | number | boolean | null | number[]): string => {
  if (input == null) return ''; // Handles null and undefined

  if (input == undefined) return '';

  const str = Array.isArray(input) ? input.join(',') : String(input);

  return str
    .split('')
    .map((char) => (char >= 'A' && char <= 'Z' ? char.toLowerCase() : char))
    .join('');
};

export const toTitleCase = (str: string | null): string | null => {
  if (!str) return null;

  const string = String(str);

  return string
    ?.replace(/_id\b/gi, ' #') // Replace "_id" with " #", handling word boundaries
    ?.replace(/_/g, ' ') // Replace remaining underscores with spaces
    ?.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + safeToLowerCase(txt.substring(1));
    }); // Convert to title case
};

export const ensureHttpsPrefix = (url: string) => {
  return url.startsWith('https://') ? url : `https://${url}`;
};

export const getPrefixedUrl = (src: string, prefix: string) => {
  const match = src.match(/\/([^/]+)$/);
  if (!match) return src;

  const filename = match[1];

  // If it already starts with the prefix, don't re-prefix it
  if (filename.startsWith(prefix)) {
    return src;
  }

  const prefixedFilename = `${prefix}_${filename}`;
  return src.replace(filename, prefixedFilename);
};

export const build_search_params = (payload: AxiosResponseType) => {
  const searchParams = new URLSearchParams();

  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  return searchParams;
};
